import { useState, useEffect } from 'react'

interface UseCountUpOptions {
  target: number
  duration?: number
}

/**
 * Animates a number from 0 to the target value over a specified duration.
 * Respects 'prefers-reduced-motion' by returning the target immediately if true.
 */
export function useCountUp({ target, duration = 1000 }: UseCountUpOptions) {
  const [count, setCount] = useState(0)

  useEffect(() => {
    // Check for reduced motion
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)')
    if (mediaQuery.matches) {
      setCount(target)
      return
    }

    let startTimestamp: number | null = null
    let animationFrameId: number

    const step = (timestamp: number) => {
      if (!startTimestamp) startTimestamp = timestamp
      const progress = Math.min((timestamp - startTimestamp) / duration, 1)
      
      // Easing function (easeOutExpo)
      const easeProgress = progress === 1 ? 1 : 1 - Math.pow(2, -10 * progress)
      
      setCount(target * easeProgress)

      if (progress < 1) {
        animationFrameId = requestAnimationFrame(step)
      } else {
        setCount(target) // Ensure exact target at the end
      }
    }

    // Reset to 0 when target changes before animating
    setCount(0)
    animationFrameId = requestAnimationFrame(step)

    return () => {
      if (animationFrameId) cancelAnimationFrame(animationFrameId)
    }
  }, [target, duration])

  return count
}
